import React from 'react'

import {
  getMinimumPrice,
  getMaximumPrice,
  PriceSource
} from 'src/shared/utils/priceUtils'
import { t } from 'src/translations'
import { useFormatPrice } from 'src/lib/js/hooks/useFormatPrice'

import FormattedPrice from './FormattedPrice'

type PriceFromRangeProps = {
  price: number | string | null | undefined
  prices: PriceSource[] | null | undefined
}

/**
 * Displays the appropriate price for an item: a single formatted price when there
 * is no size-based pricing (or when min and max are equal), or a "min – max"
 * range when the item has multiple sizes with different prices.
 *
 * Uses i18n for accessibility and internationalization support.
 */
const PriceFromRange = ({ price, prices }: PriceFromRangeProps) => {
  const minPrice = getMinimumPrice(price, prices)
  const maxPrice = getMaximumPrice(price, prices)
  const minPriceFormatted = useFormatPrice(minPrice)
  const maxPriceFormatted = useFormatPrice(maxPrice)

  if (minPrice === maxPrice) {
    return <FormattedPrice value={minPrice} />
  }

  const ariaLabel = t('price.range-aria-label', {
    min: minPriceFormatted,
    max: maxPriceFormatted
  })

  const separator = t('price.range-separator')

  return (
    <span aria-label={ariaLabel}>
      <FormattedPrice value={minPrice} />
      {` ${separator} `}
      <FormattedPrice value={maxPrice} />
    </span>
  )
}

export default PriceFromRange
